import { useState } from "react";
import { Pharmacy } from "@/types";
import { usePharmacies } from "@/hooks/usePharmacies";
import "./PharmacySelector.css";

interface PharmacySelectorProps {
  currentPharmacy?: Pharmacy;
  availablePharmacies?: Pharmacy[];
  onPharmacyChange?: () => void;
}

export function PharmacySelector({
  currentPharmacy,
  availablePharmacies = [],
  onPharmacyChange,
}: PharmacySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { setActivePharmacyById, error } = usePharmacies();

  const handleSelectPharmacy = async (pharmacyId: string) => {
    if (currentPharmacy?.id === pharmacyId) {
      setIsOpen(false);
      return;
    }

    setIsSwitching(true);
    const success = await setActivePharmacyById(pharmacyId);

    if (success) {
      setIsOpen(false);
      // Always reload the page to refresh data for the new pharmacy
      window.location.reload();
    }

    setIsSwitching(false);
  };

  // Only hide if there are no pharmacies at all
  if (availablePharmacies.length === 0 && !currentPharmacy) {
    return null;
  }

  return (
    <div className="pharmacy-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="pharmacy-selector-button"
        disabled={isSwitching || !currentPharmacy}
      >
        <div className="pharmacy-info">
          <div className="pharmacy-name">
            {currentPharmacy?.name || "Loading..."}
          </div>
          <div className="pharmacy-location">
            {currentPharmacy?.location || "Please wait"}
          </div>
        </div>
        <div className="dropdown-arrow">{isOpen ? "▲" : "▼"}</div>
      </button>

      {isOpen && currentPharmacy && (
        <>
          <div
            className="pharmacy-dropdown-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="pharmacy-dropdown">
            <div className="dropdown-header">Select Pharmacy</div>
            {availablePharmacies.map((pharmacy) => (
              <button
                key={pharmacy.id}
                onClick={() => handleSelectPharmacy(pharmacy.id)}
                className={`pharmacy-option ${
                  currentPharmacy.id === pharmacy.id ? "active" : ""
                }`}
                disabled={isSwitching}
              >
                <div className="pharmacy-option-info">
                  <div className="pharmacy-option-name">{pharmacy.name}</div>
                  <div className="pharmacy-option-location">
                    {pharmacy.location}
                  </div>
                </div>
                {pharmacy.activeConsultations !== undefined && (
                  <div className="pharmacy-consultations-badge">
                    {pharmacy.activeConsultations} active
                  </div>
                )}
                {currentPharmacy.id === pharmacy.id && (
                  <div className="pharmacy-selected-badge">✓</div>
                )}
              </button>
            ))}
            {error && <div className="pharmacy-error">{error}</div>}
          </div>
        </>
      )}
    </div>
  );
}

export default PharmacySelector;
